import { Preferences } from '@capacitor/preferences'
import type { CachedUser } from 'src/auth/coldOpenPlan'

export const STAFF_HANDOFF_KIND = 'staff-handoff' as const
export const STAFF_HANDOFF_KEY = 'fuellox.impersonation.staff-handoff'

export type StaffHandoff = {
  readonly kind: typeof STAFF_HANDOFF_KIND
  readonly token: string
  readonly cachedUser: CachedUser
}

export type StaffHandoffStorePorts = {
  getNative: () => Promise<string | null>
  setNative: (raw: string) => Promise<void>
  removeNative: () => Promise<void>
  getWeb: () => string | null
  setWeb: (raw: string) => void
  removeWeb: () => void
}

export type StaffHandoffStore = {
  read: () => Promise<StaffHandoff | null>
  write: (handoff: StaffHandoff) => Promise<void>
  clear: () => Promise<void>
}

export function parseStaffHandoff (raw: string | null | undefined): StaffHandoff | null {
  if (typeof raw !== 'string' || raw === '') {
    return null
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null
  }
  const record = parsed as Record<string, unknown>
  if (record.kind !== STAFF_HANDOFF_KIND) {
    return null
  }
  if (typeof record.token !== 'string' || record.token.trim() === '') {
    return null
  }
  if (
    record.cachedUser === null
    || typeof record.cachedUser !== 'object'
    || Array.isArray(record.cachedUser)
  ) {
    return null
  }
  return {
    kind: STAFF_HANDOFF_KIND,
    token: record.token.trim(),
    cachedUser: record.cachedUser as CachedUser,
  }
}

export function defaultStaffHandoffStorePorts (): StaffHandoffStorePorts {
  return {
    async getNative () {
      try {
        const { value } = await Preferences.get({ key: STAFF_HANDOFF_KEY })
        return value
      } catch {
        return null
      }
    },
    async setNative (raw) {
      try {
        await Preferences.set({ key: STAFF_HANDOFF_KEY, value: raw })
      } catch {
        /* Preferences is native-only; ignore on web and in unit tests. */
      }
    },
    async removeNative () {
      try {
        await Preferences.remove({ key: STAFF_HANDOFF_KEY })
      } catch {
        /* Preferences is native-only; ignore on web and in unit tests. */
      }
    },
    getWeb () {
      try {
        return globalThis.localStorage?.getItem(STAFF_HANDOFF_KEY) ?? null
      } catch {
        return null
      }
    },
    setWeb (raw) {
      try {
        globalThis.localStorage.setItem(STAFF_HANDOFF_KEY, raw)
      } catch {
        return
      }
    },
    removeWeb () {
      try {
        globalThis.localStorage.removeItem(STAFF_HANDOFF_KEY)
      } catch {
        return
      }
    },
  }
}

export function createStaffHandoffStore (
  ports: StaffHandoffStorePorts = defaultStaffHandoffStorePorts(),
): StaffHandoffStore {
  return {
    async read () {
      const native = parseStaffHandoff(await ports.getNative())
      if (native !== null) {
        return native
      }
      return parseStaffHandoff(ports.getWeb())
    },
    async write (handoff) {
      const raw = JSON.stringify({
        kind: STAFF_HANDOFF_KIND,
        token: handoff.token,
        cachedUser: handoff.cachedUser,
      })
      await ports.setNative(raw)
      ports.setWeb(raw)
    },
    async clear () {
      await ports.removeNative()
      ports.removeWeb()
    },
  }
}
